/*******************************************************
* Theme Context Provider
* 
* React context provider that orchestrates theme management.
* Handles system preference detection, theme persistence, hydration-safe
* state management, and provides theme switching capabilities throughout
* the application. Integrates with Material-UI's theme system.
* 
* Key Features:
* - System theme preference detection and synchronization
* - Theme persistence across sessions
* - Hydration-safe state management
* - Material-UI theme integration
* - Dynamic theme switching capabilities
********************************************************/
'use client';

//########## IMPORTS ##########
// #region IMPORTS
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { ThemeProvider as MUIThemeProvider, createTheme, type PaletteMode } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { getDesignTokens } from './theme';
//#endregion


//########## TYPE DEFINITIONS ##########
//#region TYPE DEFINITIONS
interface ThemeContextType {
    mode: PaletteMode;
    themeName: string;
    toggleColorMode: () => void;
    setThemeName: (name: string) => void;
    setMode: (mode: PaletteMode) => void;
}

interface ThemeProviderProps {
    children: React.ReactNode;
}
//#endregion


//########## CONSTANTS & Global Variables ##########
//#region CONSTANTS
const ThemeContext = createContext<ThemeContextType>({
    mode: 'light',
    themeName: 'TRUE2',
    toggleColorMode: () => undefined,
    setThemeName: () => undefined,
    setMode: () => undefined,
});
//#endregion


//########## FUNCTIONS ##########
// #region FUNCTIONS
export const useAppTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useAppTheme must be used within a ThemeProvider');
    }
    return context;
};
//#endregion


export default function ThemeContextProvider({ children }: ThemeProviderProps) {
    //########## MAIN LOGIC ##########
    // #region MAIN LOGIC
    // Default to 'light' so children render immediately (no blank screen).
    // The useEffect below will sync to the user's saved preference or system setting.
    const [mode, setMode] = useState<PaletteMode>('light');
    const [themeName, setThemeName] = useState<string>('TRUE2');

    useEffect(() => {
        // Move localStorage check to client-side
        const savedMode = localStorage.getItem('themeMode') as PaletteMode;
        const savedThemeName = localStorage.getItem('themeName');

        if (savedMode && (savedMode === 'light' || savedMode === 'dark')) {
            setMode(savedMode);
        } else {
            const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            setMode(systemPrefersDark ? 'dark' : 'light');
        }

        if (savedThemeName) {
            setThemeName(savedThemeName);
        }

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e: MediaQueryListEvent) => {
            if (!localStorage.getItem('themeMode')) {
                setMode(e.matches ? 'dark' : 'light');
            }
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const toggleColorMode = useCallback(() => {
        const newMode = mode === 'light' ? 'dark' : 'light';
        setMode(newMode);
        localStorage.setItem('themeMode', newMode);
    }, [mode]);

    const handleThemeNameChange = (name: string) => {
        setThemeName(name);
        localStorage.setItem('themeName', name);
    };

    const theme = useMemo(
        () => createTheme(getDesignTokens(mode, themeName)),
        [mode, themeName]
    );

    const contextValue = useMemo(
        () => ({
            mode,
            themeName,
            toggleColorMode,
            setThemeName: handleThemeNameChange,
            setMode: (newMode: PaletteMode) => {
                setMode(newMode);
                localStorage.setItem('themeMode', newMode);
            }
        }),
        [mode, themeName, toggleColorMode]
    );
    //#endregion


    //########## MARKDOWN ##########
    // #region MARKDOWN
    return (
        <ThemeContext.Provider value={contextValue}>
            <MUIThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </MUIThemeProvider>
        </ThemeContext.Provider>
    );
    //#endregion
}
